import { Pressable, View } from "react-native";
import React from "react";
import {
  AccountMemberRequest,
  claimGiftRequest,
  useFetchGiftDetailsQuery,
} from "queries/account.queries";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import { useMutation } from "@tanstack/react-query";
import useCurrentUser from "hooks/useCurrentUser";
import toast from "utils/toast";
import { toDashboard, toLogOut } from "utils/navigationUtils";
import { useAccount } from "hooks/useAccount";
import range from "lodash/range";
import { Field, FieldArray, Formik } from "formik";
import * as yup from "yup";
import TextInput from "components/TextInput";
import { RenderUserFields } from "components/NewMemberForm";
import useTenantUserFields from "hooks/useTenantUserFields";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import { getPlanType } from "utils/planPackageUtils";
import { convertUserToUserFields } from "utils/userUtils";

interface Props {
  code: string;
}

const AuthenticatedFlow: React.FC<Props> = (props) => {
  const { code } = props;
  const { updateFetchAccountBffCache } = useAccount();
  const { data: giftDetails, isFetching: fetchingGift } =
    useFetchGiftDetailsQuery(code);
  const { mutateAsync: claimGift, isLoading } = useMutation(claimGiftRequest);
  const { currentUser } = useCurrentUser();

  const {
    tenantUserFields,
    fieldRefs: tenantUserFieldRefs,
    tenantFieldsValidation,
  } = useTenantUserFields();
  const { planPackageUserFields, fieldRefs: planPackageUserFieldRefs } =
    usePlanPackageUserFields(giftDetails?.planPackage.id);

  const planType = giftDetails
    ? getPlanType(giftDetails?.planPackage)
    : undefined;

  const accountMemberRequestValidationSchema = yup.object({
    members: yup
      .array()
      .of(
        yup.object().shape({
          firstName: yup.string().required().label("First Name"),
          lastName: yup.string().required().label("Last Name"),
          email: yup.string().email().required().label("Email"),
          ...tenantFieldsValidation,
        })
      )
      .min(giftDetails ? giftDetails.quantity - 1 : 0),
  });

  const handleClaimReservationGift = async (values: {
    members: AccountMemberRequest[];
  }) => {
    const {
      quantity,
      planPackage: { id },
    } = giftDetails!;

    // Don't need to specify members when claiming VOUCHER or BANK gifts
    const members: AccountMemberRequest[] | undefined =
      planType === "RESERVATION"
        ? values.members.map((member) => ({
            ...member,
            type: "PRIMARY",
            planPackageIds: [id],
            userFields: {
              ...convertUserToUserFields({
                user: member,
                tenantOrPlanPackageUserFields: tenantUserFields!,
              }),
              ...convertUserToUserFields({
                user: member,
                tenantOrPlanPackageUserFields: planPackageUserFields!,
              }),
            },
          }))
        : undefined;

    const account = await claimGift({
      code,
      primaryUser: {
        userId: currentUser!.id,
        planPackageIds:
          // When claiming RESERVATION gifts, the primary user claims one, and each of the additional member claims one
          // For BANK or VOUCHER, primary user claims all
          planType === "RESERVATION" ? [id] : new Array(quantity).fill(id),
      },
      members,
      source: "FANRALLY_APP",
    });
    updateFetchAccountBffCache(account);
    postClaimGift();
  };

  const handleClaimVoucherAndBankGifts = async () => {
    const {
      quantity,
      planPackage: { id },
    } = giftDetails!;

    const account = await claimGift({
      code,
      primaryUser: {
        userId: currentUser!.id,
        planPackageIds: new Array(quantity).fill(id),
      },
      source: "FANRALLY_APP",
    });
    updateFetchAccountBffCache(account);
    postClaimGift();
  };

  const postClaimGift = () => {
    toDashboard();
    toast.success(
      "Thanks for claiming your gift. Click Book Seats to start making reservations for games."
    );
  };
  if (fetchingGift) {
    return <Text category="h6">Loading...</Text>;
  }

  return (
    <View>
      {/* if gift quantity is greater than 1 and is RESERVATION plan,
      then collect additional members' info */}
      {giftDetails && (
        <>
          {giftDetails.quantity > 1 &&
          getPlanType(giftDetails.planPackage) === "RESERVATION" ? (
            <View>
              <Text>
                Enter additional member{giftDetails.quantity > 2 ? "s" : ""}{" "}
                info:
              </Text>
              <Formik
                validationSchema={accountMemberRequestValidationSchema}
                initialValues={{ members: [] }}
                onSubmit={handleClaimReservationGift}
                isInitialValid={false}
              >
                {({ isValid, handleSubmit }) => {
                  return (
                    <FieldArray
                      name="members"
                      render={() => (
                        <View className="mt-4 md:border md:p-8 pt-4 md:rounded-xl md:bg-gray-50">
                          {range(0, giftDetails.quantity - 1).map(
                            (_, index) => {
                              return (
                                <View
                                  key={index}
                                  className="my-4 border rounded-lg p-4 border-slate-400"
                                >
                                  <View className="grid grid-cols-2 gap-2">
                                    <Field
                                      name={`members[${index}].firstName`}
                                      component={TextInput}
                                      label="First Name"
                                    ></Field>
                                    <Field
                                      name={`members[${index}].lastName`}
                                      component={TextInput}
                                      label="Last Name"
                                    ></Field>
                                  </View>
                                  <Field
                                    name={`members[${index}].email`}
                                    component={TextInput}
                                    label="Email"
                                  ></Field>
                                  {tenantUserFields?.map(
                                    (tenantUserField, i) => {
                                      const {
                                        field: { name, type },
                                        id,
                                      } = tenantUserField;
                                      return (
                                        <RenderUserFields
                                          key={id}
                                          name={`members[${index}].${name}`}
                                          type={type}
                                          userField={tenantUserField}
                                          index={i}
                                          userFieldRefs={tenantUserFieldRefs}
                                          nextUserFieldRefs={
                                            planPackageUserFieldRefs
                                          }
                                        />
                                      );
                                    }
                                  )}
                                  {planPackageUserFields?.map(
                                    (planPackageUserField, i) => {
                                      const {
                                        field: { name, type },
                                        id,
                                      } = planPackageUserField;
                                      return (
                                        <RenderUserFields
                                          key={id}
                                          name={`members[${index}].${name}`}
                                          type={type}
                                          userField={planPackageUserField}
                                          index={i}
                                          userFieldRefs={
                                            planPackageUserFieldRefs
                                          }
                                        />
                                      );
                                    }
                                  )}
                                </View>
                              );
                            }
                          )}

                          <View className="p-4">
                            <Button
                              onPress={() => handleSubmit()}
                              loading={isLoading}
                              disabled={!isValid}
                            >
                              Claim
                            </Button>
                          </View>
                        </View>
                      )}
                    ></FieldArray>
                  );
                }}
              </Formik>
            </View>
          ) : (
            <Button
              onPress={handleClaimVoucherAndBankGifts}
              loading={isLoading}
            >
              Claim
            </Button>
          )}
        </>
      )}

      <View className="text-center mt-4">
        <Text>
          Logged in as:{" "}
          <Text category="s1">{currentUser?.firstName as string}</Text>. Not
          you?{" "}
          <Pressable onPress={toLogOut}>
            <Text status="info">Log out</Text>
          </Pressable>
        </Text>
      </View>
    </View>
  );
};

export default AuthenticatedFlow;
